<template>

  <div>
    <v-dialog v-model="isDisplayed" :persistent="true" max-width="700px">
      <v-card>
        <p class="dialog-title">{{ dialogTitle }}</p>
        <v-card-text>
          <v-container fluid="fluid" grid-list-xl="grid-list-xl">

            <!-- TITLE -->
            <v-flex>
              <v-text-field v-model="editedClip.title" label="Title" placeholder="Title"></v-text-field>
            </v-flex>

            <!-- CAPTION -->
            <v-flex>
              <v-textarea v-model="editedClip.caption" label="Caption" placeholder="Caption" rows="1"
                auto-grow></v-textarea>
            </v-flex>

            <!-- SPORT -->
            <v-flex>
              <SportSelector ref="sportSelector" :currentSportIDs="editedClip.sportIDs" label="Sports" :multiple="true"
                @sportSelected="sportSelected" />
            </v-flex>

            <!-- LINKED VIDEOS -->
            <v-flex>
              <VideoPicker ref="videoPicker" @videoSelected="videoSelected" @videoRemoved="videoRemoved" />
            </v-flex>

            <!-- CLUBS/TEAMS -->
            <v-flex>
              <ClubTeamPicker ref="clubTeamPicker" @clubSelected="clubSelected" @clubRemoved="clubRemoved"
                @primaryClubChange="primaryClubChange" @teamSelected="teamSelected" @teamRemoved="teamRemoved"
                @primaryTeamChange="primaryTeamChange" />
            </v-flex>

            <!-- VIDEO UPLOAD -->
            <div v-if="dialogState === 'create'">
              <VideoUploader ref="videoUploader" :label="'Upload video (.mp4)'" @videoUploaded="videoUploaded" />
            </div>

            <p class="body-2 font-weight-bold mt-4">Clip options</p>

            <!-- SCHEDULE -->
            <ExpendableOption label="Schedule" v-model="scheduleToggled" :disabled="!isScheduleEditable">
              <v-card>
                <v-card-text class="grey lighten-4">
                  <DateSelector ref="endDateSelector" :currentDate="editedClip.publishDate" label="Publish date"
                    @dateSelected="publishDateSelected" :disabled="!isScheduleEditable" />
                </v-card-text>
              </v-card>
            </ExpendableOption>

            <!-- CUSTOM NOTIFICATION -->
            <ExpendableOption label="Custom notification" v-model="customNotificationToggled" :disabled="!isScheduleEditable">
              <v-card>
                <v-card-text class="grey lighten-4">
                  <TranslatableInput v-model="notification.title" label="Title (required)" required :disabled="!isScheduleEditable" />
                  <TranslatableInput v-model="notification.content" label="Text" :disabled="!isScheduleEditable" />
                </v-card-text>
              </v-card>
            </ExpendableOption>

          </v-container>

          <!-- BUTTONS -->
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="primary" flat="flat" @click.native="close">Cancel</v-btn>
            <v-btn color="primary" @click.native="save">{{ dialogButton }}</v-btn>
          </v-card-actions>

          <!-- ERROR -->
          <v-flex>
            <v-spacer></v-spacer>
            <div class="error-message">{{ errorMessage }}</div>
          </v-flex>

        </v-card-text>
      </v-card>
    </v-dialog>
  </div>

</template>

<script>

import SportSelector from '../common/SportSelector'
import VideoPicker from './VideoPicker'
import ClubTeamPicker from './ClubTeamPicker'
import VideoUploader from '../common/VideoUploader'
import TranslatableInput from '../forms/TranslatableInput'
import DateSelector from '../common/DateSelector'
import ExpendableOption from '../forms/ExpendableOption.vue'

export default {
  name: 'Dialog',
  components: {
    SportSelector,
    VideoPicker,
    ClubTeamPicker,
    VideoUploader,
    TranslatableInput,
    DateSelector,
    ExpendableOption
  },
  props: [
    'dialog',
    'dialogState',
    'currentEditedClip'
  ],
  data () {
    return {
      isDisplayed: false,
      errorMessage: '',

      editedClip: {
        type: 'VIDEO',
        title: '',
        caption: '',
        publishDate: '',
        sportIDs: [],
        videosID: [],
        primaryClubsID: [],
        secondaryClubsID: [],
        primaryTeamsID: [],
        secondaryTeamsID: []
      },

      video: null,

      scheduleToggled: false,
      customNotificationToggled: false,

      notification: {
        title: null,
        content: null,
        description: null,
        disabled: false,
        imageUrl: '',
        is_auto: false,
        label: null,
        params: [],
        sport_id: null,
        topics: []
      },

      saveButtonDisabled: false
    }
  },
  mounted () {
    if (this.currentEditedClip) {
      this.setCurrentEditedClip()
    }
    if (this.dialog) {
      this.isDisplayed = true
    }
  },
  methods: {
    publishDateSelected (date) {
      this.editedClip.publishDate = date
    },
    setCurrentEditedClip () {
      this.editedClip.title = this.currentEditedClip.title
      this.editedClip.caption = this.currentEditedClip.caption
      this.editedClip.sportIDs = this.currentEditedClip.sportIDs
      this.editedClip.publishDate = this.currentEditedClip.publishDate
      this.scheduleToggled = !!this.currentEditedClip.publishDate

      if (this.currentEditedClip.notification) {
        this.editedClip.notificationID = this.currentEditedClip.notification.id
        this.notification = this.currentEditedClip.notification
        this.customNotificationToggled = true
      }

      if (this.currentEditedClip.videosID && this.currentEditedClip.videosID.length !== 0) {
        for (let videoid of this.currentEditedClip.videosID) {
          this.$refs.videoPicker.addVideo(videoid, true)
        }
      }
      if (this.currentEditedClip.teams && this.currentEditedClip.teams.length !== 0) {
        for (let team of this.currentEditedClip.teams) {
          this.$refs.clubTeamPicker.addTeam(team.id, team.isPrimary, true)
        }
      }
      if (this.currentEditedClip.clubs && this.currentEditedClip.clubs.length !== 0) {
        for (let club of this.currentEditedClip.clubs) {
          this.$refs.clubTeamPicker.addClub(club.id, club.isPrimary)
        }
      }
    },
    isValid () {
      const errors = []
      if (!this.editedClip.title) {
        errors.push('title')
      }
      if (!this.editedClip.caption) {
        errors.push('caption')
      }
      if (!this.editedClip.sportIDs.length) {
        errors.push('sports')
      }
      if (!this.currentEditedClip && !this.video) {
        errors.push('video')
      }
      if (this.scheduleToggled && !this.editedClip.publishDate) {
        errors.push('publish date')
      }
      if (this.customNotificationToggled && !this.notification.title.en) {
        errors.push('notification')
      }

      if (errors.length) {
        this.errorMessage = `Error in field: ${errors.join(', ')}`
      } else {
        this.errorMessage = ''
      }

      return !errors.length
    },

    save () {
      if (!this.saveButtonDisabled) {
        this.saveButtonDisabled = true
        setTimeout(() => {
          this.saveButtonDisabled = false
        }, 1000)

        if (!this.isValid()) return

        if (this.dialogState === 'create') {
          this.$emit('createClip', {
            clip: this.editedClip,
            video: this.video,
            notification: this.customNotificationToggled ? this.notification : null
          })
        } else {
          this.$emit('editClip', {
            clip: this.editedClip,
            notification: this.customNotificationToggled ? this.notification : null
          })
        }
      }
    },

    sportSelected (sportid) {
      this.editedClip.sportIDs = sportid
    },

    videoSelected (video, addOnlyVideo) {
      if (!this.editedClip.videosID.includes(video.id)) {
        if (!addOnlyVideo) {
          if (this.$country === 'US') {
            if (video.team1ID && (video.team1ID !== 'defaultteam000000000')) {
              this.$refs.clubTeamPicker.addTeam(video.team1ID)
            }
            if (video.team2ID && (video.team2ID !== 'defaultteam000000000')) {
              this.$refs.clubTeamPicker.addTeam(video.team2ID)
            }
          } else {
            if (video.club1ID && (video.club1ID !== 'defaultclub000000000')) {
              this.$refs.clubTeamPicker.addClub(video.club1ID)
            }
            if (video.club2ID && (video.club2ID !== 'defaultclub000000000')) {
              this.$refs.clubTeamPicker.addClub(video.club2ID)
            }
          }
        }

        this.editedClip.videosID.push(video.id)
      }
    },
    videoRemoved (video) {
      if (
        this.editedClip.primaryClubsID.includes(video.club1ID) ||
        this.editedClip.secondaryClubsID.includes(video.club1ID)
      ) {
        this.$refs.clubTeamPicker.removeClub({ id: video.club1ID })
      }
      if (
        this.editedClip.primaryClubsID.includes(video.club2ID) ||
        this.editedClip.secondaryClubsID.includes(video.club2ID)
      ) {
        this.$refs.clubTeamPicker.removeClub({ id: video.club2ID })
      }

      if (
        this.editedClip.primaryTeamsID.includes(video.team1ID) ||
        this.editedClip.secondaryTeamsID.includes(video.team1ID)
      ) {
        this.$refs.clubTeamPicker.removeTeam({ id: video.team1ID })
      }
      if (
        this.editedClip.primaryTeamsID.includes(video.team2ID) ||
        this.editedClip.secondaryTeamsID.includes(video.team2ID)
      ) {
        this.$refs.clubTeamPicker.removeTeam({ id: video.team2ID })
      }

      if (this.editedClip.videosID.includes(video.id)) {
        this.editedClip.videosID = this.editedClip.videosID.filter(videoid => videoid !== video.id)
      }
    },

    clubSelected (club) {
      if (
        !this.editedClip.primaryClubsID.includes(club.id) &&
        !this.editedClip.secondaryClubsID.includes(club.id) &&
        club.id !== 'defaultclub000000000'
      ) {
        if (club.isPrimary) {
          this.editedClip.primaryClubsID.push(club.id)
        } else {
          this.editedClip.secondaryClubsID.push(club.id)
        }
      }
    },
    clubRemoved (club) {
      if (this.editedClip.primaryClubsID.includes(club.id)) {
        this.editedClip.primaryClubsID = this.editedClip.primaryClubsID.filter(clubid => clubid !== club.id)
      }
      if (this.editedClip.secondaryClubsID.includes(club.id)) {
        this.editedClip.secondaryClubsID = this.editedClip.secondaryClubsID.filter(clubid => clubid !== club.id)
      }
    },
    primaryClubChange (params) {
      if (this.editedClip.primaryClubsID.includes(params.id) && !params.isPrimary) {
        this.editedClip.primaryClubsID = this.editedClip.primaryClubsID.filter(clubid => clubid !== params.id)
        this.editedClip.secondaryClubsID.push(params.id)
      } else if (this.editedClip.secondaryClubsID.includes(params.id) && params.isPrimary) {
        this.editedClip.secondaryClubsID = this.editedClip.secondaryClubsID.filter(clubid => clubid !== params.id)
        this.editedClip.primaryClubsID.push(params.id)
      }
    },

    teamSelected (team) {
      if (
        !this.editedClip.primaryTeamsID.includes(team.id) &&
        !this.editedClip.secondaryTeamsID.includes(team.id) &&
        team.id !== 'defaultteam000000000'
      ) {
        if (team.isPrimary) {
          this.editedClip.primaryTeamsID.push(team.id)
        } else {
          this.editedClip.secondaryTeamsID.push(team.id)
        }
      }
    },
    teamRemoved (team) {
      if (this.editedClip.primaryTeamsID.includes(team.id)) {
        this.editedClip.primaryTeamsID = this.editedClip.primaryTeamsID.filter(teamid => teamid !== team.id)
      }
      if (this.editedClip.secondaryTeamsID.includes(team.id)) {
        this.editedClip.secondaryTeamsID = this.editedClip.secondaryTeamsID.filter(teamid => teamid !== team.id)
      }
    },
    primaryTeamChange (params) {
      if (this.editedClip.primaryTeamsID.includes(params.id) && !params.isPrimary) {
        this.editedClip.primaryTeamsID = this.editedClip.primaryTeamsID.filter(teamid => teamid !== params.id)
        this.editedClip.secondaryTeamsID.push(params.id)
      } else if (this.editedClip.secondaryTeamsID.includes(params.id) && params.isPrimary) {
        this.editedClip.secondaryTeamsID = this.editedClip.secondaryTeamsID.filter(teamid => teamid !== params.id)
        this.editedClip.primaryTeamsID.push(params.id)
      }
    },

    videoUploaded (video) {
      this.video = video
    },

    close () {
      this.clear()
      this.isDisplayed = false
    },

    clear () {
      this.editedClip = {
        type: 'VIDEO',
        title: '',
        caption: '',
        publishDate: '',
        sportIDs: [],
        videosID: [],
        primaryClubsID: [],
        secondaryClubsID: [],
        primaryTeamsID: [],
        secondaryTeamsID: []
      }
      this.video = null
      this.scheduleToggled = false
      this.customNotificationToggled = false
      this.notification = {
        title: null,
        content: null,
        description: null,
        disabled: false,
        imageUrl: '',
        is_auto: false,
        label: null,
        params: [],
        sport_id: null,
        topics: []
      }
      this.$refs.sportSelector.clear()
      this.$refs.videoPicker.clear()
      this.$refs.clubTeamPicker.clear()
      if (this.$refs.videoUploader) {
        this.$refs.videoUploader.clear()
      }
    }
  },
  computed: {
    isScheduleEditable () {
      return !this.currentEditedClip || (this.scheduleToggled && this.editedClip.publishDate && new Date(this.editedClip.publishDate) > new Date())
    },
    dialogTitle () {
      return this.dialogButton + ' clip'
    },

    dialogButton () {
      return this.dialogState.charAt(0).toUpperCase() + this.dialogState.slice(1)
    }
  },
  watch: {
    'dialog' () {
      if (this.isDisplayed !== this.dialog) {
        this.isDisplayed = this.dialog
      }
    },
    'isDisplayed' () {
      if (!this.isDisplayed) {
        this.clear()
        this.$emit('dialogClosed')
      }
    },
    'currentEditedClip' () {
      if (this.currentEditedClip) {
        this.setCurrentEditedClip()
      } else {
        this.clear()
      }
    }
  }
}

</script>

<style lang="scss" scoped>
.dialog-title {
  font-size: 16px;
  text-align: center;
  font-weight: 700;
  padding-top: 20px;
}

.error-message {
  color: red;
  text-align: end;
  font-weight: 700;
}

.notification-title {
  font-weight: bold;
  margin-top: 30px;
  margin-bottom: 0;
}
</style>



// WEBPACK FOOTER //
// src/components/clips/Dialog.vue